import React, { Fragment, memo, useCallback, useState } from 'react'
import { useFormikContext } from 'formik'
import { ReviewDecisionBar } from 'src/components/document-review/review/ReviewDecisionBar'
import { AdminApproveMcDocument, AdminRejectMcDocument } from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { ApproveMcDocumentPayload, McReviewFormValues } from 'src/interfaces/documentReview'
import { resolveMcApproveBlockReason } from 'src/utils/documentReviewApproval'
import {
  buildApproveMcDocumentPayload,
  buildRejectMcDocumentPayload,
} from 'src/utils/documentReviewPayload'
import { captureSentryError } from 'src/utils/sentry'
import { showToast } from 'src/utils/toast'
import { ApproveMcDocumentModal } from './ApproveMcDocumentModal'
import { RejectMcDocumentModal } from './RejectMcDocumentModal'

interface Props {
  partnerId: string
  partnerCollectedDataId: string
  reviewedByUser: string
  isSubmitting: boolean
  /** Refetches the queue and advances past this row. Called only after a successful decision. */
  onDecided(): void
  /** Advances past this row without sending any request. */
  onSkip(): void
}

/**
 * The Approve / Reject / Skip controls for the master-contractor review screen, plus their
 * confirm modals. Lives inside `McReviewForm` (via `useFormikContext`) so the gate and the
 * approve payload both read the reviewer's current, unsaved edits — not the values the form
 * loaded with.
 */
const McDecisionSectionComponent = ({
  partnerId,
  partnerCollectedDataId,
  reviewedByUser,
  isSubmitting,
  onDecided,
  onSkip,
}: Props) => {
  const { values } = useFormikContext<McReviewFormValues>()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  // POST …/approve-document and …/reject-document are separate grants (ENG-1207 part 2).
  const canApprove = useHasPermission(InternalPermission.McDocumentReviewApprove)
  const canReject = useHasPermission(InternalPermission.McDocumentReviewReject)

  const [isApproveModalOpen, setIsApproveModalOpen] = useState<boolean>(false)
  const [isRejectModalOpen, setIsRejectModalOpen] = useState<boolean>(false)

  const blockReason = resolveMcApproveBlockReason(values.expiresAt)

  const handleOpenApproveModal = useCallback(() => {
    setIsApproveModalOpen(true)
  }, [])

  const handleCloseApproveModal = useCallback(() => {
    setIsApproveModalOpen(false)
  }, [])

  const handleOpenRejectModal = useCallback(() => {
    setIsRejectModalOpen(true)
  }, [])

  const handleCloseRejectModal = useCallback(() => {
    setIsRejectModalOpen(false)
  }, [])

  const handleSkip = useCallback(() => {
    showToast('Document skipped')
    onSkip()
  }, [onSkip])

  const handleApproveConfirm = useCallback(async () => {
    // `buildApproveMcDocumentPayload` throws on a blank expiry. `blockReason` above should
    // already keep the Approve button disabled before a reviewer can reach this — this is
    // belt-and-braces. A React event handler is not caught by any error boundary, so an uncaught
    // throw here would make the Approve button appear to do nothing at all rather than surface a
    // clear failure, so it is caught and reported exactly like a failed request would be.
    let payload: ApproveMcDocumentPayload

    try {
      payload = buildApproveMcDocumentPayload({
        partnerId,
        partnerCollectedDataId,
        values,
        reviewedByUser,
      })
    } catch (error: unknown) {
      captureSentryError(error, {
        message: '[component.McDecisionSection.handleApproveConfirm] error',
      })
      showToast('Failed to approve the document', { variant: 'error' })

      return
    }

    const result = await asyncDispatchWithErrorToast(AdminApproveMcDocument.request(payload))

    // A failed dispatch already surfaced its own error toast (asyncDispatchWithErrorToast) — the
    // reviewer stays on this document instead of being advanced past one that was never actually
    // approved. `result` resolves `undefined` on success here (the action carries no success
    // payload) and `null` only on failure, so `null` is the one case to guard.
    if (result === null) {
      return
    }

    setIsApproveModalOpen(false)
    showToast('Document approved', { variant: 'success' })
    onDecided()
  }, [
    asyncDispatchWithErrorToast,
    onDecided,
    partnerCollectedDataId,
    partnerId,
    reviewedByUser,
    values,
  ])

  const handleRejectConfirm = useCallback(
    async (declineReason: string) => {
      const result = await asyncDispatchWithErrorToast(
        AdminRejectMcDocument.request(
          buildRejectMcDocumentPayload({
            partnerId,
            partnerCollectedDataId,
            values,
            reviewedByUser,
            declineReason,
          }),
        ),
      )

      if (result === null) {
        return
      }

      setIsRejectModalOpen(false)
      showToast('Document rejected', { variant: 'success' })
      onDecided()
    },
    [
      asyncDispatchWithErrorToast,
      onDecided,
      partnerCollectedDataId,
      partnerId,
      reviewedByUser,
      values,
    ],
  )

  return (
    <Fragment>
      <ReviewDecisionBar
        blockReason={blockReason}
        isSubmitting={isSubmitting}
        primaryLabel="Approve"
        secondaryLabel="Reject document"
        isApproveVisible={canApprove}
        isSecondaryVisible={canReject}
        onApprove={handleOpenApproveModal}
        onSecondary={handleOpenRejectModal}
        onSkip={handleSkip}
      />
      <ApproveMcDocumentModal
        isOpen={isApproveModalOpen}
        expiresAt={values.expiresAt}
        isSubmitting={isSubmitting}
        onConfirm={handleApproveConfirm}
        onClose={handleCloseApproveModal}
      />
      <RejectMcDocumentModal
        isOpen={isRejectModalOpen}
        isSubmitting={isSubmitting}
        onConfirm={handleRejectConfirm}
        onClose={handleCloseRejectModal}
      />
    </Fragment>
  )
}

export const McDecisionSection = memo(McDecisionSectionComponent)
